import { useEffect, useState } from 'react';
import Carregando from '@/components/Carregando';
import ListaConversas from '@/components/conversas/ListaConversas';
import Thread from '@/components/conversas/Thread';
import Composer from '@/components/conversas/Composer';
import PainelContato from '@/components/conversas/PainelContato';
import DialogoTransferencia from '@/components/conversas/DialogoTransferencia';
import DialogoEncerrar from '@/components/conversas/DialogoEncerrar';
import Dialogo from '@/components/Dialogo';
import SimuladorMensagem from '@/components/conversas/SimuladorMensagem';
import DialogoExcluirGrupo from '@/components/conversas/DialogoExcluirGrupo';
import { AVISO_RESGATE_DEMOROU, useConversas } from '@/conversas/ConversasProvider';
import { useAuth } from '@/auth/AuthProvider';
import { nomeDoContato } from '@/conversas/contato';
import { rotuloDoAtendimento } from '@/conversas/estadoAtendimento';
import AvatarContato from '@/components/conversas/AvatarContato';
import { usandoEmuladores } from '@/lib/firebase';
import AvisoCarga from '@/components/AvisoCarga';
import { tratarErro } from '@/lib/erros';
import { avisoDaPuxada } from '@/conversas/avisoPosse';
import {
  assumirConversa as assumirConversaNoServidor,
  marcarLida,
  reabrirConversa,
  resolverConversa,
} from '@/services/conversasService';

export default function ConversasPage() {
  const {
    clinicaId,
    conversa,
    carregando,
    erro,
    atendente,
    numeros,
    // ★ PC-6: a equipe diz se a clínica tem 1 SDR só (o aviso de posse some).
    equipe,
    selecionar,
    // ★ WS — por que o painel está vazio, quando há motivo melhor que "escolha uma".
    avisoSelecao,
    // ★ WT — os assuntos que não carregaram (equipe, etiquetas, respostas, números, clínica).
    falhas,
    // ★ WT 1b — a busca devolveu menos do que existe.
    buscaIncompleta,
    // ★★ WT 1b — a conversa escolhida está sendo buscada (fora das 200 assinadas).
    abrindoSelecao,
    // ★★ WV — rearma o resgate que estourou o tempo limite.
    tentarResgateDeNovo,
  } = useConversas();
  const { papel } = useAuth();
  // ★ PC-6 R5: depois do "Atender", de quem o lead ficou (a conversa é de quem atendeu).
  const [avisoPosse, setAvisoPosse] = useState(null);
  // ★ Mesmo nome e mesma forma de chamada de antes (o gesto não mudou — guarda A1/E1): só grava o aviso.
  const assumirConversa = async (cId, id) => {
    const r = await assumirConversaNoServidor(cId, id);
    setAvisoPosse(avisoDaPuxada(r, atendente?.id, equipe));
    return r;
  };

  // O painel do contato nasce FECHADO: a conversa é o que o atendente veio
  // fazer, e a coluna da direita comia largura útil da leitura o tempo todo.
  /**
   * ★★★ A1/E1 — ASSUMIR / REABRIR a partir do convite do Composer.
   *
   * ⚠⚠ **A CONFIRMAÇÃO só existe quando há OUTRA pessoa** (decisão do dono, item 4). ★ Assumir
   * uma conversa em Aguardando é o caso comum e segue **sem** pergunta, como sempre foi —
   * ⚠ perguntar ali seria atrito em cima do gesto mais frequente do dia.
   *
   * ★★ E o texto nomeia quem está com ela: *"Esta conversa está com a Jéssica"*. ⚠⚠ Um
   * *"tem certeza?"* genérico não dá a informação que faz a pessoa decidir.
   */
  const [confirmandoAssumir, setConfirmandoAssumir] = useState(null);
  const [painelAberto, setPainelAberto] = useState(false);
  function pedirAtendimento(estado) {
    if (!conversa) return;
    /**
     * ⚠⚠ ENCERRADA: **UMA chamada só**, e o motivo é do SERVIDOR, não de economia de rede.
     *
     * ★ `assumirAtendimento` já grava `status: 'aberta'` — ele reabre e assume num update.
     * ★★★ E é ele que grava o marcador "Atendimento iniciado por X" **lendo o status de antes**.
     * ⚠ Se a tela chamasse `reabrirConversa` primeiro, o servidor receberia a conversa já
     * `aberta` e o ciclo reabriria **sem pílula na thread** — o defeito que o teste pegou.
     *
     * ★ O botão "Reabrir" do cabeçalho continua existindo e continua deixando SEM dono
     * (decisão da VW): ⚠ reabrir para a fila e reabrir para atender são gestos diferentes.
     */
    if (estado === 'encerrada') {
      executar(() => assumirConversa(clinicaId, conversa.id));
      return;
    }
    // ★★ Em atendimento com OUTRA pessoa → confirma. Aguardando → direto.
    if (estado === 'de_outro') {
      setConfirmandoAssumir({ id: conversa.id, nome: conversa.responsavelNome || 'outro atendente' });
      return;
    }
    executar(() => assumirConversa(clinicaId, conversa.id));
  }

  const [transferindo, setTransferindo] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [simulando, setSimulando] = useState(false);
  const [excluindoGrupo, setExcluindoGrupo] = useState(false);
  const [acaoErro, setAcaoErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  // Trocar de conversa recolhe o painel: mantê-lo aberto levaria o atendente a
  // ler os dados do contato anterior achando que são do novo.
  useEffect(() => setPainelAberto(false), [conversa?.id]);

  // Abrir a conversa zera o contador de não lidas.
  useEffect(() => {
    if (conversa && conversa.naoLidas > 0) {
      // ★ WT: era `.catch(() => {})` — a conversa continuava não lida e ninguém sabia.
      // ⚠ Sem aviso na tela de propósito: é efeito de fundo, e o registro já basta.
      marcarLida(clinicaId, conversa.id).catch(tratarErro('marcar como lida', null, 'service'));
    }
  }, [clinicaId, conversa?.id, conversa?.naoLidas]);

  async function executar(acao) {
    setAcaoErro(null);
    setOcupado(true);
    try {
      await acao();
    } catch (e) {
      setAcaoErro(e.message ?? 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Texto de encerramento DESTE número, ou vazio.
   *
   * A mensagem é por número: a despedida da recepção não é a mesma do canal de
   * captação. Sem texto configurado não há o que perguntar.
   */
  const textoEncerramento = String(
    numeros.find((n) => n.id === conversa?.numeroId)?.encerramento ?? '',
  ).trim();

  const encerrar = (enviarEncerramento) =>
    executar(async () => {
      await resolverConversa({ clinicaId, conversaId: conversa.id, enviarEncerramento });
      setEncerrando(false);
    });

  /**
   * Sem texto configurado, encerra direto — perguntar "quer enviar?" sobre uma
   * mensagem que não existe seria um passo a mais para responder sempre "não".
   */
  const pedirEncerramento = () => {
    setAcaoErro(null);
    if (textoEncerramento) setEncerrando(true);
    else encerrar(false);
  };

  const meu = conversa?.responsavelId === atendente.id;

  /**
   * Quem pode apagar um grupo de vez.
   *
   * Esconder o botão evita o clique errado — NÃO é a segurança. A callable
   * `excluirGrupo` recusa por conta própria quem não é gestor e qualquer
   * conversa que não seja grupo; sem isso, bastaria chamar a função direto.
   */
  const podeExcluirGrupo =
    conversa?.ehGrupo === true && (papel === 'gestor' || papel === 'superadmin');

  return (
    <div className="pagina inbox">
      {/* Sem cabeçalho de página: o canal aberto já está marcado no menu da
          esquerda, e repetir o nome aqui só custava altura — a faixa ficava
          espremida contra o topo do chat, cortada. O que sobra é a barra de
          desenvolvimento, que existe apenas no emulador. */}
      {usandoEmuladores && (
        <div className="inbox-barra-dev">
          <button
            type="button"
            className="btn-secundario compacto"
            onClick={() => setSimulando(true)}
            title="Injeta uma mensagem fake pelo mesmo caminho dos webhooks reais"
          >
            Simular mensagem
          </button>
        </div>
      )}

      {erro && <p className="erro">{erro}</p>}
      {acaoErro && <p className="erro">{acaoErro}</p>}
      {avisoPosse && <p className="aviso" data-campo="aviso-posse">{avisoPosse}</p>}
      {/* ★★ WT — um aviso por assunto que não carregou. ⚠ Antes estes cinco listeners
          falhavam com `() => {}` e a tela só mostrava menos coisa. */}
      {falhas.map((assunto) => <AvisoCarga key={assunto} oQue={assunto} />)}
      {/* ★★ WT 1b — busca PARCIAL: uma das consultas foi recusada e a lista veio menor.
          ⚠ Não dá para mostrar "nada encontrado" nem fingir que está completa. */}
      {buscaIncompleta && (
        <p className="aviso-carga" role="alert">Resultados incompletos — recarregue a página.</p>
      )}

      {carregando ? (
        <Carregando texto="Carregando conversas…" />
      ) : (
        <div
          className={
            conversa
              ? painelAberto
                ? 'inbox-grade com-conversa com-painel'
                : 'inbox-grade com-conversa'
              : 'inbox-grade'
          }
        >
          <ListaConversas />

          {conversa ? (
            <section className="inbox-conversa">
              <header className="conversa-cabecalho">
                {/* A foto ao lado do nome: quem atende várias conversas ao mesmo
                    tempo reconhece a pessoa pela cara antes de ler o nome. Sem
                    foto, o `AvatarContato` cai nas iniciais coloridas — o mesmo
                    desenho da lista, para o rosto ser o MESMO nos dois lugares. */}
                <AvatarContato
                  foto={conversa.contatoFoto}
                  nome={nomeDoContato(conversa)}
                  tamanho={38}
                />
                <div>
                  {/* O nome é o botão do painel: é onde a pessoa já olha para
                      saber com quem está falando, então é onde ela procura os
                      dados do contato. */}
                  <button
                    type="button"
                    className="botao-nome-contato"
                    onClick={() => setPainelAberto((a) => !a)}
                    aria-expanded={painelAberto}
                    title={painelAberto ? 'Fechar os dados do contato' : 'Ver os dados do contato'}
                  >
                    <strong>{nomeDoContato(conversa)}</strong>
                  </button>
                  <span className="painel-sub">
                    {/* VW: encerrada não tem dono — "Encerrada por X" vem de resolvidaPorNome. */}
                    {rotuloDoAtendimento(conversa)}
                    {!conversa.ehGrupo &&
                      conversa.atendimento === 'aguardando_ia' &&
                      ' · destinada à SDR IA'}
                  </span>
                </div>

                {/* GRUPO não se assume, não se transfere e não se encerra:
                    são fluxos de atendimento de UMA pessoa. Mostrar os botões
                    ofereceria ações que o produto não modela para grupo. */}
                <div className="item-acoes">
                  {!conversa.ehGrupo && !meu && (
                    <button
                      type="button"
                      className="btn-secundario"
                      disabled={ocupado}
                      onClick={() =>
                        executar(() => assumirConversa(clinicaId, conversa.id, atendente))
                      }
                    >
                      Assumir
                    </button>
                  )}
                  {!conversa.ehGrupo && (
                    <button
                      type="button"
                      className="btn-secundario"
                      disabled={ocupado}
                      onClick={() => setTransferindo(true)}
                    >
                      Transferir
                    </button>
                  )}
                  {/* Só para grupo, e só para gestor. Fica por último e em
                      vermelho: é o único ato irreversível da tela. */}
                  {podeExcluirGrupo && (
                    <button
                      type="button"
                      className="btn-destrutivo compacto"
                      disabled={ocupado}
                      onClick={() => setExcluindoGrupo(true)}
                      title="Apagar este grupo e todas as mensagens, para sempre"
                    >
                      Excluir grupo
                    </button>
                  )}
                  {conversa.ehGrupo ? null : conversa.status === 'resolvida' ? (
                    <button
                      type="button"
                      className="btn-secundario"
                      disabled={ocupado}
                      onClick={() => executar(() => reabrirConversa(clinicaId, conversa.id))}
                    >
                      Reabrir
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-secundario"
                      disabled={ocupado}
                      onClick={pedirEncerramento}
                    >
                      {/* Só o rótulo: a ação segue chamando resolverConversa e
                          o status gravado continua 'resolvida'. */}
                      Encerrar
                    </button>
                  )}
                </div>
              </header>

              <Thread />
              <Composer aoPedirAtendimento={pedirAtendimento} />
            </section>
          ) : (
            <section className="inbox-conversa vazia">
              {/* ★★ WS — quem chegou aqui clicando num LEAD não escolheu nada: para essa
                  pessoa "Escolha uma conversa à esquerda" é uma frase falsa, e foi ela que
                  o dono viu duas vezes sem entender. ★ Quando a seleção aponta para uma
                  conversa que não é deste canal (ou não existe), o aviso diz isso. */}
              {/* ★★★ WT — "Escolha uma conversa" SÓ quando a lista carregou.
                  ⚠⚠ Com a lista falhada, aquela frase manda a pessoa escolher entre nada e
                  sugere que a falta é escolha dela. ★ A ordem importa: `erro` (a lista não
                  veio) vence `avisoSelecao` (a seleção não casou), que vence o padrão. */}
              {/* ★★★ WT 1b — "ABRINDO A CONVERSA…" no intervalo do resgate por id.
                  ⚠⚠ Antes, a tela caía na frase padrão enquanto buscava a conversa fora das
                  200 — e *"Escolha uma conversa à esquerda"* para quem **acabou de
                  escolher** é a tela chamando a pessoa de distraída. ★ Foi o que fez o dono
                  relatar como defeito algo que ia funcionar um segundo depois (caso Dalva:
                  a 231ª de 1.147). ★ A ordem: lista falhada > abrindo > seleção que não
                  casa > a frase padrão. */}
              {erro ? (
                <AvisoCarga oQue="as conversas" />
              ) : abrindoSelecao ? (
                <p className="vazio" role="status">Abrindo a conversa…</p>
              ) : avisoSelecao === AVISO_RESGATE_DEMOROU ? (
                /**
                 * ★★★ WV — A SAÍDA DO LIMBO.
                 *
                 * ⚠⚠ Antes, *"Abrindo a conversa…"* **não tinha fim em caso nenhum**: o
                 * dono ficou "um tempão" nela e a conversa não abriu (lead Rafa, 08/out).
                 * ★ Dez segundos depois a tela passa a dizer o que aconteceu **e oferece o
                 * que a pessoa fez à mão**: tentar outra vez.
                 *
                 * ★ `role="status"` aqui também: quem usa leitor de tela precisa saber que
                 * o painel mudou de "abrindo" para "não abriu".
                 */
                <div className="vazio conversa-nao-abriu" role="status">
                  <p>{AVISO_RESGATE_DEMOROU}</p>
                  <button type="button" className="msg-midia-tentar" onClick={tentarResgateDeNovo}>
                    Tentar de novo
                  </button>
                </div>
              ) : (
                <p className="vazio">
                  {avisoSelecao ?? (
                    `Escolha uma conversa à esquerda${
                      usandoEmuladores ? ' — ou use "Simular mensagem" para criar uma.' : '.'
                    }`
                  )}
                </p>
              )}
            </section>
          )}

          {conversa && painelAberto && <PainelContato aoFechar={() => setPainelAberto(false)} />}
        </div>
      )}

      {/**
        * ★★★ A1/E1 — a confirmação de assumir conversa de OUTRA pessoa (item 4).
        *
        * ⚠⚠ Ela existe porque o complemento de 09/out torna o "Assumir" **rotina**: se enviar
        * exige estar em atendimento, então ajudar passa a significar **tirar da colega**.
        * ★★ O servidor registra a troca na timeline; ★ a tela avisa **antes**.
        */}
      <Dialogo
        titulo="Assumir a conversa"
        aberto={Boolean(confirmandoAssumir)}
        aoFechar={() => setConfirmandoAssumir(null)}
        largura={420}
      >
        <p>
          Esta conversa está com <strong>{confirmandoAssumir?.nome}</strong>. Assumir mesmo assim?
        </p>
        <p className="campo-ajuda">
          A troca fica registrada na conversa.
        </p>
        <div className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={() => setConfirmandoAssumir(null)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primario"
            disabled={ocupado}
            onClick={() => {
              const alvo = confirmandoAssumir;
              setConfirmandoAssumir(null);
              executar(() => assumirConversa(clinicaId, alvo.id));
            }}
          >
            Assumir
          </button>
        </div>
      </Dialogo>

      {conversa && (
        <DialogoTransferencia aberto={transferindo} aoFechar={() => setTransferindo(false)} />
      )}
      <DialogoEncerrar
        aberto={encerrando}
        texto={textoEncerramento}
        aoFechar={() => setEncerrando(false)}
        aoDecidir={encerrar}
      />

      <SimuladorMensagem aberto={simulando} aoFechar={() => setSimulando(false)} />

      {/* Depois de excluir, a conversa é DESELECIONADA: o listener ao vivo a
          tira da lista sozinho, mas a thread continuaria aberta apontando para
          um documento que não existe mais. */}
      {podeExcluirGrupo && (
        <DialogoExcluirGrupo
          aberto={excluindoGrupo}
          aoFechar={() => setExcluindoGrupo(false)}
          aoExcluir={() => {
            setExcluindoGrupo(false);
            selecionar(null);
          }}
        />
      )}
    </div>
  );
}
